import { FormattingIssue } from '../types';

/**
 * Validates HCL formatting and returns detailed issues with line numbers.
 * This is a local, client-side validator that doesn't require any API.
 */
export const validateHCLFormatting = (
  content: string,
  fileName: 'provider' | 'main' | 'overrides'
): FormattingIssue[] => {
  const issues: FormattingIssue[] = [];
  const lines = content.split('\n');

  lines.forEach((line, index) => {
    const lineNum = index + 1;

    // Check 1: Inconsistent indentation (should be 2 spaces)
    const leadingSpaces = line.match(/^( +)/)?.[1].length || 0;
    if (leadingSpaces > 0 && leadingSpaces % 2 !== 0) {
      issues.push({
        file: fileName,
        line: lineNum,
        issue: `Inconsistent indentation (${leadingSpaces} spaces)`,
        suggestion: 'Use 2-space indentation',
      });
    }

    // Check 2: Tabs instead of spaces
    if (line.includes('\t')) {
      issues.push({
        file: fileName,
        line: lineNum,
        issue: 'Contains tabs instead of spaces',
        suggestion: 'Replace tabs with 2 spaces',
      });
    }

    // Check 3: Trailing whitespace
    if (line.match(/\s+$/)) {
      issues.push({
        file: fileName,
        line: lineNum,
        issue: 'Trailing whitespace detected',
        suggestion: 'Remove trailing spaces',
      });
    }

    // Check 4: Missing spaces around = in assignments
    if (line.match(/\w+=\w+/) && !line.includes('==')) {
      issues.push({
        file: fileName,
        line: lineNum,
        issue: 'Missing spaces around assignment operator',
        suggestion: 'Use "key = value" format',
      });
    }

    // Check 5: Multiple blank lines
    if (index > 0 && lines[index - 1].trim() === '' && line.trim() === '') {
      issues.push({
        file: fileName,
        line: lineNum,
        issue: 'Multiple consecutive blank lines',
        suggestion: 'Use single blank line for separation',
      });
    }

    // Check 6: Opening brace not on same line
    const trimmed = line.trim();
    if (index > 0 && trimmed === '{') {
      const prevLine = lines[index - 1].trim();
      if (prevLine && !prevLine.endsWith('{')) {
        issues.push({
          file: fileName,
          line: lineNum,
          issue: 'Opening brace should be on same line as declaration',
          suggestion: 'Move { to end of previous line',
        });
      }
    }

    // Check 7: Missing blank line before block
    if (
      index > 0 &&
      (trimmed.startsWith('resource ') ||
        trimmed.startsWith('module ') ||
        trimmed.startsWith('variable ') ||
        trimmed.startsWith('provider '))
    ) {
      const prevLine = lines[index - 1].trim();
      if (prevLine && prevLine !== '' && !prevLine.endsWith('{')) {
        issues.push({
          file: fileName,
          line: lineNum,
          issue: 'Missing blank line before block declaration',
          suggestion: 'Add blank line before this block',
        });
      }
    }

    // Check 8: Comments not properly formatted
    if (trimmed.startsWith('#') || trimmed.startsWith('//')) {
      if (!line.match(/^[\s]*(#|\/\/) /)) {
        issues.push({
          file: fileName,
          line: lineNum,
          issue: 'Comment missing space after # or //',
          suggestion: 'Use "# comment" or "// comment" format',
        });
      }
    }
  });

  return issues;
};

/**
 * Formats HCL content according to terraform fmt standards.
 * Returns formatted string or null if unable to format.
 */
export const formatHCL = (content: string): string => {
  let formatted = content;

  // Step 1: Normalize line endings
  formatted = formatted.replace(/\r\n/g, '\n');

  // Step 2: Remove trailing whitespace
  formatted = formatted
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n');

  // Step 3: Add spaces around = in assignments
  formatted = formatted.replace(/(\w+)=(\w+)/g, '$1 = $2');
  formatted = formatted.replace(/(\w+)= (\w+)/g, '$1 = $2');
  formatted = formatted.replace(/(\w+) =(\w+)/g, '$1 = $2');

  // Step 4: Ensure proper spacing after #
  formatted = formatted.replace(/#(\S)/g, '# $1');
  formatted = formatted.replace(/\/\/(\S)/g, '// $1');

  // Step 5: Ensure blank line before major blocks
  formatted = formatted.replace(
    /\n(resource|module|variable|provider|terraform|data|output)\s+/g,
    '\n\n$1 '
  );

  // Step 6: Collapse multiple blank lines to single (must run after Step 5)
  formatted = formatted.replace(/\n{3,}/g, '\n\n');

  // Step 7: Remove blank lines at start
  formatted = formatted.replace(/^\n+/, '');

  // Step 8: Ensure file ends with single newline
  formatted = formatted.trimEnd() + '\n';

  return formatted;
};

/**
 * Checks if content needs formatting by comparing with formatted version.
 */
export const needsFormatting = (content: string): boolean => {
  const formatted = formatHCL(content);
  return content !== formatted;
};

/**
 * Provides a diff summary of what would change if formatted.
 */
export const getFormattingDiff = (content: string): string => {
  const formatted = formatHCL(content);
  if (content === formatted) {
    return 'No formatting changes needed ✓';
  }

  const originalLines = content.split('\n');
  const formattedLines = formatted.split('\n');

  let changes = 0;
  const maxLines = Math.max(originalLines.length, formattedLines.length);

  for (let i = 0; i < maxLines; i++) {
    if (originalLines[i] !== formattedLines[i]) {
      changes++;
    }
  }

  return `Would change ${changes} line(s)`;
};
